/**
 * Normalize public table sheet API payload (guest axios may already unwrap .data).
 * @param {unknown} raw
 * @returns {import("../types/tableSheet").TableSheetResponse | null}
 */
export function unwrapTableSheetResponse(raw) {
  if (!raw || typeof raw !== "object") return null;
  if (raw.club && Array.isArray(raw.events)) return raw;
  const nested = raw.data;
  if (nested && typeof nested === "object" && nested.club && Array.isArray(nested.events)) {
    return nested;
  }
  return null;
}

/**
 * @param {import("../types/tableSheet").TableSheetShareLinkResponse | unknown} raw
 */
export function unwrapTableSheetShareLink(raw) {
  if (!raw || typeof raw !== "object") return null;
  if (raw.shareUrl && raw.slug) return raw;
  const nested = raw.data;
  if (nested && typeof nested === "object" && nested.shareUrl) return nested;
  return null;
}

const SECTION_COLOR_CLASS = {
  purple: "table-sheet-section--purple",
  green: "table-sheet-section--green",
  blue: "table-sheet-section--blue",
  orange: "table-sheet-section--orange",
  red: "table-sheet-section--red",
  yellow: "table-sheet-section--yellow",
  pink: "table-sheet-section--pink",
  teal: "table-sheet-section--teal",
};

/** @param {string | undefined} color */
export function sectionColorClass(color) {
  const key = String(color || "").trim().toLowerCase();
  return SECTION_COLOR_CLASS[key] || "table-sheet-section--default";
}

/** @param {import("../types/tableSheet").TableSheetSection} section */
export function sectionHeaderLabel(section) {
  const code = String(section?.code || "").trim();
  if (code) return `${code} SECTION`;
  return String(section?.label || "SECTION").trim() || "SECTION";
}

/** @param {import("../types/tableSheet").TableSheetTable} table */
export function tableCodeLabel(table) {
  return String(table?.code || table?.label || "").trim() || "—";
}

/**
 * Normalize API table status for comparisons.
 * @param {string | undefined} status
 */
export function normalizeTableSheetStatus(status) {
  return String(status || "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");
}

export function isTableOfflineSoldOut(table) {
  if (!table || typeof table !== "object") return false;
  const flag = table.offlineSoldOut ?? table.offline_sold_out;
  return flag === true || flag === "true" || flag === 1 || flag === "1";
}

export function isTableOnlineSoldOut(table) {
  if (!table || typeof table !== "object") return false;
  const flag = table.onlineSoldOut ?? table.online_sold_out;
  return flag === true || flag === "true" || flag === 1 || flag === "1";
}

/**
 * Staff-hold statuses from API (new + legacy).
 * UI must always display as "Hold", never raw held_for_employee.
 * @param {string | undefined} status
 */
export function isHoldStatus(status) {
  const s = normalizeTableSheetStatus(status);
  return (
    s === "hold" ||
    s === "held" ||
    s === "held_for_employee" ||
    s === "heldforemployee"
  );
}

/**
 * PENDING_PAYMENT window — status "processing" (priority over sold_out / Hold).
 * Also accepts common aliases the API may send.
 * @param {string | undefined} status
 */
export function isProcessingStatus(status) {
  const s = normalizeTableSheetStatus(status);
  return (
    s === "processing" ||
    s === "pending_payment" ||
    s === "pendingpayment" ||
    s === "payment_processing" ||
    s === "paymentprocessing"
  );
}

/**
 * Resolve paymentProcessing payload (camel or snake_case).
 * @param {import("../types/tableSheet").TableSheetTable | undefined} table
 * @returns {Record<string, unknown> | null}
 */
export function getPaymentProcessingPayload(table) {
  if (!table || typeof table !== "object") return null;
  const nested =
    table.paymentProcessing ?? table.payment_processing ?? null;
  if (nested && typeof nested === "object") return nested;
  return null;
}

/**
 * True when the table is in PENDING_PAYMENT processing.
 * Takes priority over sold_out / Hold — including when status is still
 * sold_out/Hold but paymentProcessing is present.
 * @param {import("../types/tableSheet").TableSheetTable | undefined} table
 */
export function isTableProcessing(table) {
  if (!table || typeof table !== "object") return false;
  const status = normalizeTableSheetStatus(table.status);
  // Confirmed booking always wins.
  if (status === "booked") return false;
  if (isProcessingStatus(table.status)) return true;

  const payload = getPaymentProcessingPayload(table);
  if (!payload) return false;
  const hasMarker =
    payload.bookingId != null ||
    payload.booking_id != null ||
    payload.bookingref != null ||
    payload.bookingRef != null ||
    payload.booking_ref != null ||
    payload.holdExpiresAt != null ||
    payload.hold_expires_at != null ||
    payload.holdRemainingMinutes != null ||
    payload.hold_remaining_minutes != null;
  return hasMarker;
}

/**
 * Resolve hold payload from employeeHold / snake_case / flat table fields.
 * @param {import("../types/tableSheet").TableSheetTable | undefined} table
 * @returns {Record<string, unknown> | null}
 */
export function getEmployeeHoldPayload(table) {
  if (!table || typeof table !== "object") return null;
  const nested =
    table.employeeHold ??
    table.employee_hold ??
    table.hold ??
    table.heldBy ??
    table.held_by ??
    null;
  if (nested && typeof nested === "object") return nested;

  // Flat fields on the table row itself
  const hasFlat =
    table.empName != null ||
    table.emp_name != null ||
    table.empref != null ||
    table.empRef != null ||
    table.emp_ref != null ||
    table.holdRemainingMinutes != null ||
    table.hold_remaining_minutes != null ||
    table.holdExpiresAt != null ||
    table.hold_expires_at != null ||
    table.holdId != null ||
    table.hold_id != null;
  return hasFlat ? table : null;
}

/**
 * Collect candidate objects that may carry staff name / empref for a hold.
 * API may put name on employeeHold.empName, or only on sibling employee.fullname
 * (same shape as booked.booking.employee).
 * @param {import("../types/tableSheet").TableSheetTable | undefined} table
 * @returns {Record<string, unknown>[]}
 */
function holdIdentitySources(table) {
  const hold = getEmployeeHoldPayload(table);
  const sources = [];
  const push = (obj) => {
    if (obj && typeof obj === "object" && !sources.includes(obj)) {
      sources.push(obj);
    }
  };
  push(hold);
  if (hold) {
    push(hold.employee);
    push(hold.user);
    push(hold.heldBy);
    push(hold.held_by);
    push(hold.staff);
  }
  if (table && typeof table === "object") {
    push(table.employee);
    push(table.user);
    push(table.heldBy);
    push(table.held_by);
    push(table.staff);
  }
  return sources;
}

/**
 * @param {Record<string, unknown> | null | undefined} obj
 */
function pickNameFromIdentity(obj) {
  if (!obj || typeof obj !== "object") return "";
  return String(
    obj.empName ??
      obj.emp_name ??
      obj.employeeName ??
      obj.employee_name ??
      obj.staffName ??
      obj.holderName ??
      obj.fullname ??
      obj.fullName ??
      obj.name ??
      ""
  ).trim();
}

/**
 * @param {Record<string, unknown> | null | undefined} obj
 */
function pickEmprefFromIdentity(obj) {
  if (!obj || typeof obj !== "object") return "";
  return String(obj.empref ?? obj.empRef ?? obj.emp_ref ?? "").trim();
}

/**
 * @param {Record<string, unknown> | null | undefined} hold
 */
function pickHoldEmpName(hold) {
  if (!hold || typeof hold !== "object") return "";
  const nestedEmp =
    hold.employee && typeof hold.employee === "object" ? hold.employee : null;
  const nestedUser =
    hold.user && typeof hold.user === "object" ? hold.user : null;
  return (
    pickNameFromIdentity(hold) ||
    pickNameFromIdentity(nestedEmp) ||
    pickNameFromIdentity(nestedUser) ||
    ""
  );
}

/**
 * @param {Record<string, unknown> | null | undefined} hold
 */
function pickHoldEmpref(hold) {
  if (!hold || typeof hold !== "object") return "";
  const nestedEmp =
    hold.employee && typeof hold.employee === "object" ? hold.employee : null;
  const nestedUser =
    hold.user && typeof hold.user === "object" ? hold.user : null;
  return (
    pickEmprefFromIdentity(hold) ||
    pickEmprefFromIdentity(nestedEmp) ||
    pickEmprefFromIdentity(nestedUser) ||
    ""
  );
}

/**
 * Index staff display names from booked/released rows on the sheet.
 * Used when employeeHold only has empref / heldForUserId (no empName).
 * @param {import("../types/tableSheet").TableSheetResponse["sheet"] | null | undefined} sheet
 * @returns {{ byEmpref: Map<string, string>, byUserId: Map<string, string> }}
 */
export function buildSheetStaffNameIndex(sheet) {
  /** @type {Map<string, string>} */
  const byEmpref = new Map();
  /** @type {Map<string, string>} */
  const byUserId = new Map();

  const remember = (emp) => {
    if (!emp || typeof emp !== "object") return;
    const name = pickNameFromIdentity(emp);
    if (!name) return;
    const ref = pickEmprefFromIdentity(emp);
    const userId = String(emp.userId ?? emp.userid ?? emp._id ?? "").trim();
    if (ref) {
      const key = ref.toLowerCase();
      if (!byEmpref.has(key)) byEmpref.set(key, name);
    }
    if (userId && !byUserId.has(userId)) byUserId.set(userId, name);
  };

  for (const section of sheet?.sections ?? []) {
    for (const table of section?.tables ?? []) {
      remember(table?.booking?.employee);
      remember(table?.employee);
      const released = Array.isArray(table?.releasedBookings)
        ? table.releasedBookings
        : [];
      for (const rb of released) remember(rb?.employee);
    }
  }

  return { byEmpref, byUserId };
}

/**
 * Who holds the table: always prefer "empName (empref)".
 * e.g. "Gaurav Sharma (GS01)" / "baller emp (ROJO1161)"
 * When API omits empName, resolve name via staffIndex (sheet bookings) using
 * empref or heldForUserId.
 * @param {import("../types/tableSheet").TableSheetTable | undefined} table
 * @param {{ byEmpref?: Map<string, string>, byUserId?: Map<string, string> } | null} [staffIndex]
 */
export function pickEmployeeHoldLabel(table, staffIndex = null) {
  const sources = holdIdentitySources(table);
  if (!sources.length && !staffIndex) return "";

  let name = "";
  let ref = "";
  for (const src of sources) {
    if (!name) name = pickNameFromIdentity(src);
    if (!ref) ref = pickEmprefFromIdentity(src);
    if (name && ref) break;
  }

  const hold = getEmployeeHoldPayload(table);
  const heldForUserId = String(
    hold?.heldForUserId ?? hold?.held_for_user_id ?? ""
  ).trim();

  if (!name && staffIndex) {
    if (ref && staffIndex.byEmpref?.has(ref.toLowerCase())) {
      name = staffIndex.byEmpref.get(ref.toLowerCase()) || "";
    }
    if (!name && heldForUserId && staffIndex.byUserId?.has(heldForUserId)) {
      name = staffIndex.byUserId.get(heldForUserId) || "";
    }
  }

  // Don't treat empref as a display name (avoids "ROJO1161 (ROJO1161)").
  if (name && ref && name.toLowerCase() === ref.toLowerCase()) {
    name = "";
  }

  if (name && ref) return `${name} (${ref})`;
  if (name) return name;
  if (ref) return ref;
  return "";
}

/**
 * True when the table is on a staff hold.
 * Matches status "Hold" and legacy held_for_employee / held.
 * Not bookable / not available.
 * Processing (PENDING_PAYMENT) takes priority and is never treated as Hold.
 * @param {import("../types/tableSheet").TableSheetTable | undefined} table
 */
export function isTableOnHold(table) {
  if (isTableProcessing(table)) return false;
  if (isHoldStatus(table?.status)) return true;
  const hold = getEmployeeHoldPayload(table);
  if (!hold) return false;
  const hasHoldMarker =
    hold.holdId != null ||
    hold.hold_id != null ||
    pickHoldEmpName(hold) ||
    pickHoldEmpref(hold) ||
    hold.holdExpiresAt != null ||
    hold.hold_expires_at != null ||
    hold.holdRemainingMinutes != null ||
    hold.hold_remaining_minutes != null;
  if (hasHoldMarker && normalizeTableSheetStatus(table?.status) !== "booked") {
    return true;
  }
  return false;
}

/**
 * Remaining minutes from paymentProcessing.holdRemainingMinutes / holdExpiresAt.
 * @param {import("../types/tableSheet").TableSheetTable | undefined} table
 * @returns {number | null}
 */
export function getProcessingRemainingMinutes(table) {
  const payload = getPaymentProcessingPayload(table);
  if (!payload) return null;
  const raw = payload.holdRemainingMinutes ?? payload.hold_remaining_minutes;
  if (raw != null && raw !== "") {
    const minutes = Number(raw);
    if (Number.isFinite(minutes)) return Math.max(0, Math.round(minutes));
  }
  const expiresAt = payload.holdExpiresAt ?? payload.hold_expires_at;
  if (expiresAt) {
    const expires = new Date(expiresAt).getTime();
    if (!Number.isNaN(expires)) {
      const mins = Math.ceil((expires - Date.now()) / 60_000);
      return Math.max(0, mins);
    }
  }
  return null;
}

/**
 * Format paymentProcessing.holdExpiresAt for display, e.g. "11:30".
 * @param {import("../types/tableSheet").TableSheetTable | undefined} table
 */
export function formatProcessingExpiresAtDisplay(table) {
  const payload = getPaymentProcessingPayload(table);
  const iso = payload?.holdExpiresAt ?? payload?.hold_expires_at;
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
  });
}

/**
 * Remaining minutes from holdRemainingMinutes, or derived from holdExpiresAt.
 * @param {import("../types/tableSheet").TableSheetTable | undefined} table
 * @returns {number | null}
 */
export function getHoldRemainingMinutes(table) {
  const hold = getEmployeeHoldPayload(table);
  if (!hold) return null;
  const raw = hold.holdRemainingMinutes ?? hold.hold_remaining_minutes;
  if (raw != null && raw !== "") {
    const minutes = Number(raw);
    if (Number.isFinite(minutes)) return Math.max(0, Math.round(minutes));
  }
  const expiresAt = hold.holdExpiresAt ?? hold.hold_expires_at;
  if (expiresAt) {
    const expires = new Date(expiresAt).getTime();
    if (!Number.isNaN(expires)) {
      const mins = Math.ceil((expires - Date.now()) / 60_000);
      return Math.max(0, mins);
    }
  }
  return null;
}

/**
 * Total hold duration minutes from holdDurationMinutes when present.
 * @param {import("../types/tableSheet").TableSheetTable | undefined} table
 * @returns {number | null}
 */
export function getHoldDurationMinutes(table) {
  const hold = getEmployeeHoldPayload(table);
  if (!hold) return null;
  const raw = hold.holdDurationMinutes ?? hold.hold_duration_minutes;
  if (raw == null || raw === "") return null;
  const minutes = Number(raw);
  if (!Number.isFinite(minutes)) return null;
  return Math.max(0, Math.round(minutes));
}

/**
 * Format holdExpiresAt for display, e.g. "11:30".
 * @param {import("../types/tableSheet").TableSheetTable | undefined} table
 */
export function formatHoldExpiresAtDisplay(table) {
  const hold = getEmployeeHoldPayload(table);
  const iso = hold?.holdExpiresAt ?? hold?.hold_expires_at;
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
  });
}

/**
 * @param {(key: string, opts?: object) => string} [t]
 */
function holdTranslateFallback(t) {
  if (typeof t === "function") return t;
  return (key, opts) => {
    if (key === "TABLE_SHEET_HOLD_MIN_LEFT") {
      return `${opts?.minutes ?? 0} min left`;
    }
    if (key === "TABLE_SHEET_HOLD_DURATION") {
      return `${opts?.minutes ?? 0} min hold`;
    }
    if (key === "TABLE_SHEET_HOLD_UNTIL") {
      return `until ${opts?.time ?? ""}`;
    }
    if (key === "TABLE_SHEET_HOLD") return "Hold";
    if (key === "TABLE_SHEET_PROCESSING") return "Processing";
    return key;
  };
}

/**
 * Time-left label from employeeHold.holdRemainingMinutes.
 * e.g. "85 min left"
 * @param {import("../types/tableSheet").TableSheetTable | undefined} table
 * @param {(key: string, opts?: object) => string} [t]
 */
export function tableHoldRemainingLabel(table, t) {
  const translate = holdTranslateFallback(t);
  const minutes = getHoldRemainingMinutes(table);
  if (minutes == null) return "";
  return translate("TABLE_SHEET_HOLD_MIN_LEFT", { minutes });
}

/**
 * Optional duration label — "120 min hold".
 * @param {import("../types/tableSheet").TableSheetTable | undefined} table
 * @param {(key: string, opts?: object) => string} [t]
 */
export function tableHoldDurationLabel(table, t) {
  const translate = holdTranslateFallback(t);
  const minutes = getHoldDurationMinutes(table);
  if (minutes == null) return "";
  return translate("TABLE_SHEET_HOLD_DURATION", { minutes });
}

/**
 * Optional end-time label — "until 11:30".
 * @param {import("../types/tableSheet").TableSheetTable | undefined} table
 * @param {(key: string, opts?: object) => string} [t]
 */
export function tableHoldExpiresLabel(table, t) {
  const translate = holdTranslateFallback(t);
  const time = formatHoldExpiresAtDisplay(table);
  if (!time) return "";
  return translate("TABLE_SHEET_HOLD_UNTIL", { time });
}

/**
 * Detail line for Hold rows: who · time left.
 * Always surfaces empName (empref) when present.
 * @param {import("../types/tableSheet").TableSheetTable | undefined} table
 * @param {(key: string, opts?: object) => string} [t]
 * @param {{ byEmpref?: Map<string, string>, byUserId?: Map<string, string> } | null} [staffIndex]
 */
export function tableHoldDetailLabel(table, t, staffIndex = null) {
  const who = pickEmployeeHoldLabel(table, staffIndex);
  const timeLeft = tableHoldRemainingLabel(table, t);
  const parts = [];
  if (who) parts.push(who);
  if (timeLeft) parts.push(timeLeft);
  return parts.length ? parts.join(" · ") : "—";
}

/**
 * Tooltip / secondary meta: duration + expires + note.
 * @param {import("../types/tableSheet").TableSheetTable | undefined} table
 * @param {(key: string, opts?: object) => string} [t]
 * @param {{ byEmpref?: Map<string, string>, byUserId?: Map<string, string> } | null} [staffIndex]
 */
export function tableHoldMetaTitle(table, t, staffIndex = null) {
  const hold = getEmployeeHoldPayload(table);
  const parts = [
    pickEmployeeHoldLabel(table, staffIndex),
    tableHoldRemainingLabel(table, t),
    tableHoldDurationLabel(table, t),
    tableHoldExpiresLabel(table, t),
    String(hold?.note ?? "").trim(),
  ].filter(Boolean);
  return parts.join(" · ");
}

/**
 * Status badge for Hold rows — exact "Hold".
 * @param {(key: string) => string} [t]
 */
export function tableHoldStatusLabel(t) {
  const translate = typeof t === "function" ? t : () => "Hold";
  return translate("TABLE_SHEET_HOLD");
}

/**
 * Status badge for processing rows — exact "Processing".
 * @param {(key: string) => string} [t]
 */
export function tableProcessingStatusLabel(t) {
  const translate = typeof t === "function" ? t : () => "Processing";
  return translate("TABLE_SHEET_PROCESSING");
}

/**
 * Time-left label from paymentProcessing.holdRemainingMinutes.
 * @param {import("../types/tableSheet").TableSheetTable | undefined} table
 * @param {(key: string, opts?: object) => string} [t]
 */
export function tableProcessingRemainingLabel(table, t) {
  const translate = holdTranslateFallback(t);
  const minutes = getProcessingRemainingMinutes(table);
  if (minutes == null) return "";
  return translate("TABLE_SHEET_HOLD_MIN_LEFT", { minutes });
}

/**
 * Optional end-time label for payment processing — "until 11:30".
 * @param {import("../types/tableSheet").TableSheetTable | undefined} table
 * @param {(key: string, opts?: object) => string} [t]
 */
export function tableProcessingExpiresLabel(table, t) {
  const translate = holdTranslateFallback(t);
  const time = formatProcessingExpiresAtDisplay(table);
  if (!time) return "";
  return translate("TABLE_SHEET_HOLD_UNTIL", { time });
}

/**
 * Detail / tooltip for processing rows: bookingref · time left · until.
 * @param {import("../types/tableSheet").TableSheetTable | undefined} table
 * @param {(key: string, opts?: object) => string} [t]
 */
export function tableProcessingMetaTitle(table, t) {
  const payload = getPaymentProcessingPayload(table);
  const ref = String(
    payload?.bookingref ?? payload?.bookingRef ?? payload?.booking_ref ?? ""
  ).trim();
  const parts = [
    ref,
    tableProcessingRemainingLabel(table, t),
    tableProcessingExpiresLabel(table, t),
  ].filter(Boolean);
  return parts.join(" · ");
}

/**
 * Numeric spend for a sheet booking — prefer exact amount fields, then minimumSpend.
 * @param {import("../types/tableSheet").TableSheetBooking | import("../types/tableSheet").TableSheetReleasedBooking | undefined} booking
 * @returns {number | null}
 */
export function pickBookingSpendAmount(booking) {
  if (!booking || typeof booking !== "object") return null;
  const candidates = [
    booking.amount,
    booking.totalAmount,
    booking.total_amount,
    booking.paidAmount,
    booking.paid_amount,
    booking.minimumSpend,
    booking.minimum_spend,
  ];
  for (const raw of candidates) {
    if (raw == null || raw === "") continue;
    const n = Number(raw);
    if (Number.isFinite(n) && n >= 0) return n;
  }
  return null;
}

/**
 * Compact amount label with K notation.
 * 5000 → "5K", 4500 → "4.5K", 3200 → "3.2K", 99 → "99"
 * Falls back to amountDisplay only when no numeric spend exists.
 * @param {import("../types/tableSheet").TableSheetBooking | import("../types/tableSheet").TableSheetReleasedBooking | undefined} booking
 */
export function formatBookingAmountDisplay(booking) {
  const n = pickBookingSpendAmount(booking);
  if (n != null) {
    if (n === 0) return "0";
    if (n >= 1000) {
      const k = n / 1000;
      // One decimal when needed: 4.5K / 3.2K; exact thousands: 5K
      const text = Number.isInteger(k)
        ? String(k)
        : (Math.round(k * 10) / 10)
            .toFixed(1)
            .replace(/\.0$/, "");
      return `${text}K`;
    }
    if (Number.isInteger(n)) return String(n);
    return String(Math.round(n * 100) / 100);
  }
  return String(booking?.amountDisplay ?? "").trim();
}

/**
 * Gender / party segment for sheet labels: "1M-1F" or fallback "4 Pax".
 * @param {import("../types/tableSheet").TableSheetBooking | import("../types/tableSheet").TableSheetReleasedBooking | undefined} booking
 * @param {(key: string) => string} [translate]
 */
export function formatBookingPaxSegment(booking, translate) {
  const t =
    typeof translate === "function"
      ? translate
      : (key) => (key === "TABLE_SHEET_PAX" ? "Pax" : key);

  if (!booking || typeof booking !== "object") return "";

  const maleRaw = booking.malePax ?? booking.male_pax;
  const femaleRaw = booking.femalePax ?? booking.female_pax;
  const hasMale = maleRaw != null && maleRaw !== "";
  const hasFemale = femaleRaw != null && femaleRaw !== "";

  if (hasMale || hasFemale) {
    const segments = [];
    if (hasMale) {
      const n = Number(maleRaw);
      if (Number.isFinite(n)) segments.push(`${Math.max(0, Math.round(n))}M`);
    }
    if (hasFemale) {
      const n = Number(femaleRaw);
      if (Number.isFinite(n)) segments.push(`${Math.max(0, Math.round(n))}F`);
    }
    if (segments.length) return segments.join("-");
  }

  const pax = booking.partySize ?? booking.party_size;
  if (pax != null && pax !== "") {
    const n = Number(pax);
    if (Number.isFinite(n)) return `${Math.max(0, Math.round(n))} ${t("TABLE_SHEET_PAX")}`;
    return `${pax} ${t("TABLE_SHEET_PAX")}`;
  }
  return "";
}

/**
 * Booked/history detail: GuestName-1M-1F-(Employee Name). Amount stays in the status column.
 * @param {import("../types/tableSheet").TableSheetBooking | import("../types/tableSheet").TableSheetReleasedBooking | undefined} booking
 * @param {(key: string) => string} [t]
 */
export function formatBookingDetailLabel(booking, t) {
  const translate =
    typeof t === "function"
      ? t
      : (key) => (key === "TABLE_SHEET_PAX" ? "Pax" : key);

  if (!booking || typeof booking !== "object") return "—";

  const name = String(booking.guestName || "").trim();
  const paxSegment = formatBookingPaxSegment(booking, translate);
  const staff = staffReferralLabel(booking);

  const parts = [];
  if (name) parts.push(name);
  if (paxSegment) parts.push(paxSegment);
  if (staff) parts.push(staff);

  return parts.length ? parts.join("-") : "—";
}

/**
 * Right-column / detail label for a table row by API status.
 * Booked rows still render guest detail + amount separately in the UI.
 * @param {import("../types/tableSheet").TableSheetTable} table
 * @param {(key: string) => string} [t]
 */
export function tableRowLabel(table, t) {
  const translate = typeof t === "function" ? t : (key, opts) => {
    if (key === "TABLE_SHEET_SOLD_OUT") return "Sold out";
    if (key === "TABLE_SHEET_UNAVAILABLE") return "Unavailable";
    if (key === "TABLE_SHEET_AVAILABLE") return "Available";
    if (key === "TABLE_SHEET_HOLD") return "Hold";
    if (key === "TABLE_SHEET_PROCESSING") return "Processing";
    if (key === "TABLE_SHEET_HOLD_MIN_LEFT") {
      return `${opts?.minutes ?? 0} min left`;
    }
    if (key === "TABLE_SHEET_PAX") return "Pax";
    return key;
  };

  if (isTableProcessing(table)) {
    return translate("TABLE_SHEET_PROCESSING");
  }

  if (isTableOnHold(table)) {
    return tableHoldDetailLabel(table, translate);
  }

  if (isTableOfflineSoldOut(table)) {
    return translate("TABLE_SHEET_SOLD_OUT");
  }

  const status = normalizeTableSheetStatus(table?.status);

  switch (status) {
    case "booked":
      return formatBookingDetailLabel(table.booking, translate);
    case "processing":
      return translate("TABLE_SHEET_PROCESSING");
    case "sold_out":
      return translate("TABLE_SHEET_SOLD_OUT");
    case "unavailable":
      return translate("TABLE_SHEET_UNAVAILABLE");
    case "available":
      return translate("TABLE_SHEET_AVAILABLE");
    default:
      // Unknown statuses must not be treated as Available.
      if (status) return String(table?.status || "").trim();
      return translate("TABLE_SHEET_AVAILABLE");
  }
}

/** @param {import("../types/tableSheet").TableSheetResponse["sheet"] | null | undefined} sheet */
export function sheetHasNewBookings(sheet) {
  if (!sheet) return false;
  if (sheet.latestBooking?.isNew === true) return true;
  const count = sheet.stats?.newBookingCount;
  if (typeof count === "number" && count > 0) return true;
  for (const section of sheet.sections ?? []) {
    for (const table of section.tables ?? []) {
      if (table.status === "booked" && table.booking?.isNew === true) {
        return true;
      }
    }
  }
  return false;
}

/**
 * True when the live sheet has in-flight activity and should poll faster
 * (processing payments, staff holds, new bookings).
 * @param {import("../types/tableSheet").TableSheetResponse["sheet"] | null | undefined} sheet
 */
export function sheetNeedsFastPoll(sheet) {
  if (!sheet) return false;
  const stats = sheet.stats || {};
  const processingCount = Number(
    stats.processingCount ?? stats.processing_count ?? 0
  );
  if (Number.isFinite(processingCount) && processingCount > 0) return true;
  if (sheetHasNewBookings(sheet)) return true;
  for (const section of sheet.sections ?? []) {
    for (const table of section.tables ?? []) {
      if (isTableProcessing(table) || isTableOnHold(table)) return true;
    }
  }
  return false;
}

/** @param {string | undefined} fullname — e.g. "Akash Shirodkar" → "AS" */
export function initialsFromFullname(fullname) {
  const parts = String(fullname ?? "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!parts.length) return "";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
}

/** @param {import("../types/tableSheet").TableSheetBooking | undefined} booking */
export function pickBookingStaffEmpref(booking) {
  if (!booking) return "";
  const emp = booking.employee;
  if (emp && typeof emp === "object") {
    const fromEmp = emp.empref ?? emp.empRef;
    if (fromEmp != null && String(fromEmp).trim()) {
      return String(fromEmp).trim();
    }
  }
  const direct = booking.empref;
  return direct != null ? String(direct).trim() : "";
}

/**
 * Staff initials for the row label — AS from employee, not empref code.
 * @param {import("../types/tableSheet").TableSheetBooking | undefined} booking
 */
export function pickStaffInitials(booking) {
  if (!booking) return "";
  const emp = booking.employee;
  if (emp && typeof emp === "object") {
    const fromEmp = emp.initials;
    if (fromEmp != null && String(fromEmp).trim()) {
      return String(fromEmp).trim().toUpperCase();
    }
    const fromName = initialsFromFullname(
      emp.fullname ?? emp.fullName ?? emp.name
    );
    if (fromName) return fromName;
  }
  const referred = booking.referredByInitials;
  if (referred != null && String(referred).trim()) {
    return String(referred).trim().toUpperCase();
  }
  return "";
}

/** @param {import("../types/tableSheet").TableSheetBooking | undefined} booking */
export function pickStaffFullName(booking) {
  if (!booking) return "";
  const emp = booking.employee;
  if (emp && typeof emp === "object") {
    const name = String(emp.fullname ?? emp.fullName ?? emp.name ?? "").trim();
    if (name) return name;
  }
  return "";
}

/**
 * Staff label for the row — full name when available; empref is not shown.
 * @param {import("../types/tableSheet").TableSheetBooking | undefined} booking
 */
export function staffReferralLabel(booking) {
  const name = pickStaffFullName(booking);
  if (name) return `(${name})`;
  const initials = pickStaffInitials(booking);
  if (initials) return `(${initials})`;
  return "";
}

/** Tooltip on booked row — full name only (empref hidden). */
export function staffReferralTitle(booking) {
  const name = pickStaffFullName(booking);
  if (name) return name;
  const initials = pickStaffInitials(booking);
  return initials || "";
}

/** @deprecated Use staffReferralLabel — kept for imports */
export function staffInitialsLabel(booking) {
  return staffReferralLabel(booking);
}

/** @param {import("../types/tableSheet").TableSheetEventOption | { displayDate?: string; startdatetime?: string; primaryServiceDate?: string } | null | undefined} event */
export function formatEventStartDateDisplay(event) {
  if (!event || typeof event !== "object") return "";
  if (event.displayDate && String(event.displayDate).trim()) {
    return String(event.displayDate).trim();
  }
  if (event.primaryServiceDate) {
    const parts = String(event.primaryServiceDate).split("-");
    if (parts.length === 3) {
      return `${parts[2]}/${parts[1]}/${parts[0]}`;
    }
    return String(event.primaryServiceDate).trim();
  }
  if (event.startdatetime) {
    const d = new Date(event.startdatetime);
    if (!Number.isNaN(d.getTime())) {
      return d.toLocaleDateString(undefined, {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
    }
  }
  return "";
}

/** @param {import("../types/tableSheet").TableSheetEventOption} event */
export function formatEventChipDate(event) {
  if (event?.primaryServiceDate) {
    const parts = String(event.primaryServiceDate).split("-");
    if (parts.length === 3) {
      return `${parts[2]}/${parts[1]}/${parts[0]}`;
    }
    return event.primaryServiceDate;
  }
  if (event?.startdatetime) {
    const d = new Date(event.startdatetime);
    if (!Number.isNaN(d.getTime())) {
      return d.toLocaleDateString(undefined, {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
    }
  }
  return "";
}

/** @param {string | undefined} iso */
export function formatGeneratedAt(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString(undefined, {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/** @param {import("../types/tableSheet").TableSheetTable | undefined} table */
export function isTableCheckedIn(table) {
  if (table?.checkedIn === true) return true;
  if (table?.booking?.checkedIn === true) return true;
  return false;
}

/**
 * Released booking history for a table, newest releasedAt first.
 * @param {import("../types/tableSheet").TableSheetTable | undefined} table
 * @returns {import("../types/tableSheet").TableSheetReleasedBooking[]}
 */
export function getReleasedBookingsNewestFirst(table) {
  const list = Array.isArray(table?.releasedBookings)
    ? table.releasedBookings.slice()
    : [];
  return list.sort((a, b) => {
    const ta = new Date(a?.releasedAt || 0).getTime();
    const tb = new Date(b?.releasedAt || 0).getTime();
    return (Number.isNaN(tb) ? 0 : tb) - (Number.isNaN(ta) ? 0 : ta);
  });
}

/**
 * Guest-gender/pax-(staff) line for current or released booking.
 * Amount stays in the row status column, not in this string.
 * @param {import("../types/tableSheet").TableSheetBooking | import("../types/tableSheet").TableSheetReleasedBooking | undefined} booking
 * @param {(key: string) => string} [t]
 */
export function bookingGuestSpendLabel(booking, t) {
  return formatBookingDetailLabel(booking, t);
}

/** @param {string | undefined} iso */
export function formatReleasedAt(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString(undefined, {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/** @param {string} slug @param {string} [eventId] */
export function getTableSheetSeenStorageKey(slug, eventId = "") {
  const s = String(slug || "").trim().toLowerCase();
  const e = String(eventId || "").trim();
  return `table-sheet-seen:${s}:${e}`;
}

/** @param {string} storageKey @returns {string[]} */
export function readSeenBookingIds(storageKey) {
  try {
    const raw = window.sessionStorage.getItem(storageKey);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.map((id) => String(id).trim()).filter(Boolean);
  } catch {
    return [];
  }
}

/** @param {string} storageKey @param {string[] | undefined} bookingIds */
export function writeSeenBookingIds(storageKey, bookingIds) {
  try {
    const ids = Array.isArray(bookingIds)
      ? bookingIds.map((id) => String(id).trim()).filter(Boolean)
      : [];
    window.sessionStorage.setItem(storageKey, JSON.stringify(ids));
  } catch {
    /* sessionStorage unavailable */
  }
}

/** @param {string | undefined} status */
export function eventStatusBadgeClass(status) {
  const s = String(status || "").trim().toLowerCase();
  if (s === "ongoing" || s === "live") return "table-sheet-event-badge--ongoing";
  if (s === "upcoming") return "table-sheet-event-badge--upcoming";
  return "table-sheet-event-badge--default";
}
